import { PointTypes } from '@oneblink/types'
import {
  getPointAddressLocalityName,
  SubmissionPointAddress,
} from '../../point-address'

type FixedPremise = {
  fixedPremise__sectionLabel__exclude?: string
  nswFoodLicenceApplicationFormFixedPremises?: {
    Trading_Name?: string
    Address_For_Service_of_Notices?: string
    Primary_Manual_Address: boolean
    Suburb: string
    Facility_Address: SubmissionPointAddress
  }
}

type Vehicle = {
  nswFoodLicenceApplicationFormVehicle?: {
    Location_of_vehicles: Array<{
      Vehicle_details?: Array<{
        vehicle__sectionLabel__exclude?: string
        Registration_Number?: string
      }>
    }>
  }
}

type FishingBusiness = {
  nswFoodLicenceApplicationFormFishing?: {
    fishingBusiness__sectionLabel__exclude?: string
    NSWCommercialFishing?: string
    FishingVesselRepeatable?: Array<{
      vessel__sectionLabel__exclude?: string
      'NSW_food_licence_application_form_|_Vessel'?: {
        Vessel_Name?: string
      }
    }>
    BeachHaulingRepeatable?: Array<{
      beachEstuary__sectionLabel__exclude?: string
      'NSW_food_licence_application_form_|_Beach_/_estuary'?: {
        Region?: string
      }
    }>
  }
}

function updateFixedPremiseLabels(submission: Record<string, unknown>) {
  const fixedPremises = (submission.fixedPremises || []) as FixedPremise[]

  fixedPremises.forEach((fixedPremise) => {
    const fixedPremiseForm =
      fixedPremise?.nswFoodLicenceApplicationFormFixedPremises

    if (!fixedPremiseForm) {
      return
    }

    if (!fixedPremiseForm.Trading_Name) {
      fixedPremise.fixedPremise__sectionLabel__exclude = 'Fixed Premises'
      return fixedPremise
    }

    let newLabel = fixedPremiseForm.Trading_Name

    if (fixedPremiseForm.Address_For_Service_of_Notices === 'Yes') {
      if (submission.Manual_Address && submission.Suburb) {
        newLabel += ': ' + submission.Suburb
      } else if (!submission.Manual_Address && submission.Address) {
        const address = submission.Address as PointTypes.PointAddress
        newLabel += ': ' + (getPointAddressLocalityName(address) || '')
      }
    } else {
      if (fixedPremiseForm.Primary_Manual_Address && fixedPremiseForm.Suburb) {
        newLabel += ': ' + fixedPremiseForm.Suburb
      } else if (
        !fixedPremiseForm.Primary_Manual_Address &&
        fixedPremiseForm.Facility_Address
      ) {
        newLabel +=
          ': ' +
          (getPointAddressLocalityName(fixedPremiseForm.Facility_Address) || '')
      }
    }

    fixedPremise.fixedPremise__sectionLabel__exclude = newLabel
  })
}

function updateVehicleLabels(submission: Record<string, unknown>) {
  const vehiclesFacilities = (submission.vehicle || []) as Vehicle[]

  vehiclesFacilities.forEach((vehiclesFacility) => {
    const vehiclesLocations =
      vehiclesFacility?.nswFoodLicenceApplicationFormVehicle
        ?.Location_of_vehicles || []

    vehiclesLocations.forEach((vehiclesLocation) => {
      const vehicleDetails = vehiclesLocation.Vehicle_details || []

      vehicleDetails.forEach((vehicle) => {
        vehicle.vehicle__sectionLabel__exclude = 'Vehicle'

        if (vehicle.Registration_Number) {
          vehicle.vehicle__sectionLabel__exclude +=
            ': ' + vehicle.Registration_Number
        }
      })
    })
  })
}

function updateFishingBusinessLabels(submission: Record<string, unknown>) {
  updateFishingHeadingLabel(submission)
  updateVesselLabels(submission)
  updateBeachHaulingLabels(submission)
}

// fishing business helpers
function updateFishingHeadingLabel(submission: Record<string, unknown>) {
  const fishingFacilities = (submission.foodBusiness || []) as FishingBusiness[]

  fishingFacilities.forEach((fishingFacility) => {
    const fishingForm = fishingFacility?.nswFoodLicenceApplicationFormFishing

    if (fishingForm) {
      fishingForm.fishingBusiness__sectionLabel__exclude = 'Fishing Business'
      if (fishingForm?.NSWCommercialFishing) {
        fishingForm.fishingBusiness__sectionLabel__exclude +=
          ' FB: ' + fishingForm.NSWCommercialFishing
      }
    }
  })
}

function updateVesselLabels(submission: Record<string, unknown>) {
  const fishingFacilities = (submission.foodBusiness || []) as FishingBusiness[]

  fishingFacilities.forEach((fishingFacility) => {
    const vesselRepeatable =
      fishingFacility?.nswFoodLicenceApplicationFormFishing
        ?.FishingVesselRepeatable || []

    vesselRepeatable.forEach((vessel) => {
      const vesselName =
        vessel['NSW_food_licence_application_form_|_Vessel']?.Vessel_Name

      vessel.vessel__sectionLabel__exclude = 'Vessel'
      if (vesselName) {
        vessel.vessel__sectionLabel__exclude += ': ' + vesselName
      }
    })
  })
}

function updateBeachHaulingLabels(submission: Record<string, unknown>) {
  const fishingFacilities = (submission.foodBusiness || []) as FishingBusiness[]

  fishingFacilities.forEach((fishingFacility) => {
    const beachHaulingRepeatable =
      fishingFacility?.nswFoodLicenceApplicationFormFishing
        ?.BeachHaulingRepeatable || []

    beachHaulingRepeatable.forEach((beach) => {
      const regionName =
        beach['NSW_food_licence_application_form_|_Beach_/_estuary']?.Region
      beach.beachEstuary__sectionLabel__exclude = 'Beach Hauling'
      if (regionName) {
        beach.beachEstuary__sectionLabel__exclude += ': ' + regionName
      }
    })
  })
}

export default function updateFacilityLabels(
  submission: Record<string, unknown>,
) {
  updateFixedPremiseLabels(submission)
  updateVehicleLabels(submission)
  updateFishingBusinessLabels(submission)
}
